import { SnackbarMessageProps } from "@knowfully/ui.common";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import ChangeReasonDialog from "components/ChangeReasonDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import SnackbarMessage from "components/SnackbarMessage";
import { isValid } from "date-fns";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { Navigate, useParams } from "react-router-dom";
import {
  CancelSkuUserAgreementRequest,
  ResponseCode,
  ResponseOfAgreementResponse,
  ResponseOfSkus,
  ResponseOfSkuUserAgreementInfo,
} from "services/KnowfullyAdminClient";
import { dateToStandardFormat } from "utils/dateUtils";
import { UserAgreementPreviewBox } from "./UserAgreementPreviewBox";

const UserAgreementDetails = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const [previewOpen, previewOpenActions] = useBoolean(false);
  const [confirmCancelAgreement, confirmCancelAgreementActions] = useBoolean(
    false
  );
  const queryClient = useQueryClient();

  const {
    skuUserAgreementService,
    skuService,
    agreementService,
    skuUserAgreementsUrl,
  } = useGlobalContext();
  const [changeReason, setChangeReason] = useState("");
  const [cancelAgreementLoading, cancelAgreementLoadingActions] = useBoolean(
    false
  );

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const skuUserAgreementResult = useStrictQuery<ResponseOfSkuUserAgreementInfo>(
    ["getSkuUserAgreementById", id],
    () => skuUserAgreementService.getSkuUserAgreementById(id!),
    {
      enabled: !!id,
    }
  );

  const agreementResponse = useStrictQuery<ResponseOfAgreementResponse>(
    [
      "agreementService.getAgreement",
      skuUserAgreementResult?.data?.data?.agreementId!,
    ],
    () =>
      agreementService.getAgreement(
        skuUserAgreementResult?.data?.data?.agreementId!
      ),
    {
      enabled: skuUserAgreementResult.isFetched,
    }
  );

  const skuUserAgreement = skuUserAgreementResult.data?.data!;
  const title = t(
    { id: "userAgreementTitle" },
    { name: skuUserAgreement?.userName }
  );
  useTitle(title);

  const skuResult = useStrictQuery<ResponseOfSkus>(
    ["sku", skuUserAgreement?.skuId],
    () => skuService.getSkuById(skuUserAgreement?.skuId!),
    {
      enabled: !!skuUserAgreement && !skuUserAgreementResult.isLoading,
    }
  );

  if (!id) return <Navigate to={skuUserAgreementsUrl()} replace />;
  if (
    agreementResponse.isLoading ||
    skuUserAgreementResult.isLoading ||
    skuResult.isLoading
  ) {
    return <Loader />;
  }

  const sku = skuResult?.data?.data!;
  const manualReplacements = agreementResponse?.data?.data?.manualReplacements;
  const parsedManualReplacements = manualReplacements
    ? JSON.parse(manualReplacements)
    : null;

  const rows = [
    {
      title: t({ id: "id" }),
      component: skuUserAgreement.id && <span>{skuUserAgreement.id}</span>,
    },
    RowElement(
      "statusLabel",
      skuUserAgreement?.statusName ?? "",
      skuUserAgreementResult.isLoading
    ),
    RowElement("skuLabel", sku?.sku ?? "", skuResult.isLoading),
    RowElement(
      "userNameLabel",
      skuUserAgreement?.userName ?? "",
      skuResult.isLoading
    ),
    RowElement(
      "dueDateLabel",
      skuUserAgreement?.dueDate!,
      skuUserAgreementResult.isLoading
    ),
    RowElement(
      "agreement",
      skuUserAgreement?.agreementName ?? "",
      skuUserAgreementResult.isLoading
    ),
    ...(skuUserAgreement?.skuUserAgreementValues?.map((x) => {
      const displayValue = Array.isArray(parsedManualReplacements?.template)
        ? parsedManualReplacements.template.find((t: any) => t.key === x.name)
            ?.display_value
        : x.name;

      return RowElement(
        `User input: ${displayValue}`,
        x.type === "date" && isValid((x.value as any) as Date)
          ? dateToStandardFormat((x.value as any) as Date)
          : x.value?.toString() ?? "",
        skuUserAgreementResult.isLoading,
        undefined,
        false
      );
    }) ?? []),
  ].filter((r) => !!r.component);

  const onCancelAgreement = async () => {
    cancelAgreementLoadingActions.setTrue();
    const response = await skuUserAgreementService.cancelSkuUserAgreement(
      new CancelSkuUserAgreementRequest({
        skuUserAgreementId: id,
        changeReason,
      })
    );

    if (response.responseCode === ResponseCode.OK) {
      await onCancelAgreementSuccess();
    }
  };

  const onCancelAgreementSuccess = async () => {
    //after message is sent, waits for 5 seconds to invalidate the query.
    await new Promise((f) => setTimeout(f, 5000));
    queryClient.invalidateQueries(["getSkuUserAgreementById", id]);
    queryClient.invalidateQueries("getSkuUserAgreements");
    queryClient.invalidateQueries("skuUserAgreementService.getSkuUserAgreementAgreements");

    setSnackbarMessage(
      new SnackbarMessageProps(
        t({ id: "cancelSkuUserAgreementSuccessMessage" })
      )
    );

    confirmCancelAgreementActions.setFalse();
    cancelAgreementLoadingActions.setFalse();
  };

  return (
    <>
      <RedirectBackButton
        redirectRoute={skuUserAgreementsUrl()}
        labelKey="backToListButtonLabel"
      />
      <h1>
        <span>{title}</span>
      </h1>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell
                  component="th"
                  scope="row"
                  style={{ fontWeight: "bold" }}
                >
                  {title}
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box my={3}>
        <Grid container style={{ padding: 2, backgroundColor: "#f0f4fa" }}>
          <Grid item md={8} xs={6}>
            <Typography variant="h6" component="h6" m={1}>
              <T id="agreement" />
            </Typography>
          </Grid>
          <Grid item md={4} xs={6}>
            <IconButton
              style={{ float: "right", marginTop: "5px" }}
              onClick={() => previewOpenActions.setTrue()}
            >
              <ContentPasteSearchIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      <Box my={3}>
        <Button
          type="submit"
          color="error"
          variant="contained"
          disabled={skuUserAgreement?.statusName === "Cancelled"}
          onClick={() => confirmCancelAgreementActions.setTrue()}
          endIcon={cancelAgreementLoading && <CircularProgress size={28} />}
        >
          <T id="cancelAgreement" />
        </Button>
      </Box>

      <ChangeReasonDialog
        open={confirmCancelAgreement}
        onCancel={() => confirmCancelAgreementActions.setFalse()}
        setChangeReason={setChangeReason}
        handleConfirm={onCancelAgreement}
        changeReason={changeReason}
      />
      <UserAgreementPreviewBox
        open={previewOpen}
        skuUserAgreement={skuUserAgreement}
        previewOpenActions={previewOpenActions}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default withErrorBoundary(UserAgreementDetails);
